import { AnalyticsEvent } from "@common/constants/analytics-events";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import {
    ActionsCell,
    type DataGridCellProps,
    useCellControlTabIndex,
} from "@design-system/DataGrid";
import { trackEvent } from "@lib/analytics";
import { TaskChatButton } from "../../TaskChatButton";
import { useTaskListOpenTask } from "../../taskList.context";
import type { NestedTaskRow } from "../../types";
import { useOpenTaskSection } from "../../useOpenTaskSection";
import { TaskActionMenuItems, useTaskActions } from "../../useTaskActions";
import { hasTaskFields, type TaskListTaskRow } from "./taskRowCells";

const TaskRowActions = ({ task }: { task: TaskListTaskRow }) => {
    const openTask = useTaskListOpenTask();
    const tabIndex = useCellControlTabIndex();
    const openChat = useOpenTaskSection(openTask, "chat");
    const openDetails = useOpenTaskSection(openTask, "details");
    const { items, dialogs } = useTaskActions({
        task,
        onEditDetails: openDetails,
    });
    const unreadCount = task.unreadCount ?? 0;

    const handleChatClick = () => {
        void trackEvent(AnalyticsEvent.TASK_LIST_CHAT_CLICKED, {
            taskId: task.id,
            taskType: task.type,
            unreadCount,
        });
        openChat(task);
    };

    return (
        <ActionsCell>
            <ActionsCell.Slot reveal={unreadCount > 0 ? "always" : "hover"}>
                <TaskChatButton
                    unreadCount={unreadCount}
                    label="Open task chat"
                    tabIndex={tabIndex}
                    onClick={handleChatClick}
                />
            </ActionsCell.Slot>
            <ActionsCell.Menu aria-label="More actions">
                <TaskActionMenuItems items={items} />
            </ActionsCell.Menu>
            {dialogs}
        </ActionsCell>
    );
};

/** The Actions column: a task row's chat button and its actions menu; none on draft or busy closes demo rows. */
export const TaskActionsCell = ({
    context,
}: DataGridCellProps<NestedTaskRow>) => {
    const task = context.row.original;
    return hasTaskFields(task) &&
        !task.isNewPlaceholder &&
        !isBusyClosesDemoId(task.id) ? (
        <TaskRowActions task={task} />
    ) : null;
};
